import React, { useState, useEffect, useRef } from 'react';
import './Header.css';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import apiService from '../services/api';
// Import the logo from assets
import defaultLogoImg from '../assets/logo.png';

const NAV_ITEMS = [
  { label: 'Home', href: '/' },
  {
    label: 'About',
    href: '/about',
    sub: [
      ['About Us', '/about'],
      ['Founder Story', '/founder-story'],
      ['Our Correspondent', '/correspondence'],
      ['30 Years Trek', '/trek'],
    ],
  },
  {
    label: 'Programs',
    href: '/programs',
    sub: [
      ['Our Programs', '/programs'],
      ['Activities', '/activities'],
      ['Awards', '/awards'],
      ['Our Impact', '/impact'],
    ],
  },
  {
    label: 'Media',
    href: '/gallery',
    sub: [
      ['Gallery', '/gallery'],
      ['News', '/news'],
      ['Testimonials', '/testimonials'],
      ['Visitors Book', '/visitors-book'],
    ],
  },
  {
    label: 'Get Involved',
    href: '/volunteer',
    sub: [
      ['Volunteer', '/volunteer'],
      ['CSR Opportunities', '/csr'],
      ['Foreign Donations', '/foreign-donations'],
    ],
  },
  { label: 'Contact', href: '/contact' },
];

const IconMenu = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
);

const IconClose = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <line x1="5" y1="5" x2="19" y2="19" />
    <line x1="19" y1="5" x2="5" y2="19" />
  </svg>
);

const IconChevron = ({ open }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .25s ease' }}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

export default function Header({ 
  logoSrc = null, 
  donateHref = '/donate' 
}) {
  const [scrolled, setScrolled] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [openSub, setOpenSub] = useState(null);
  const [websiteInfo, setWebsiteInfo] = useState(null);
  const [logoError, setLogoError] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const headerRef = useRef(null);
  const navRef = useRef(null);
  const apiCalled = useRef(false);

  // Default fallback values
  const defaultLogo = defaultLogoImg; // Now using imported image
  const defaultName = 'Devnar Foundation';

  // Fetch website info with timeout and cache
  useEffect(() => {
    // Prevent multiple calls
    if (apiCalled.current) return;
    apiCalled.current = true;

    // Check localStorage cache first
    const cachedData = localStorage.getItem('headerWebsiteInfo');
    if (cachedData) {
      try {
        const parsed = JSON.parse(cachedData);
        // Check if cache is less than 1 hour old
        if (parsed.timestamp && (Date.now() - parsed.timestamp) < 3600000) {
          setWebsiteInfo(parsed.data);
          return;
        }
      } catch (e) {
        // Invalid cache, ignore
      }
    }

    const fetchWebInfo = async () => {
      try {
        // Create abort controller for timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);

        const result = await apiService.fetchWebinfo();
        clearTimeout(timeoutId);

        if (result.status === 'success' && result.data && result.data.length > 0) {
          const data = result.data[0];
          setWebsiteInfo(data);
          
          // Cache the data
          localStorage.setItem('headerWebsiteInfo', JSON.stringify({
            data: data,
            timestamp: Date.now()
          }));
        }
      } catch (error) {
        // Silent fail - use fallback
        console.debug('Using fallback header data');
      }
    };

    fetchWebInfo();
  }, []);

  // Get the logo URL with proper fallbacks
  const getLogoUrl = () => {
    // If logoError is true, use default
    if (logoError) return defaultLogo;
    
    // If logoSrc is provided as prop, use it
    if (logoSrc) return logoSrc;
    
    // If website info is loaded and has logo_url
    if (websiteInfo?.logo_url) {
      let logoUrl = websiteInfo.logo_url;
      
      // Handle different URL formats from the API response
      if (logoUrl.startsWith('uploads/')) {
        const baseUrl = websiteInfo.website_url || 'https://devnarfoundationfortheblind.org';
        return `${baseUrl.replace(/\/$/, '')}/admin/${logoUrl}`;
      }
      if (logoUrl.startsWith('/admin/') || logoUrl.startsWith('/uploads/')) {
        const baseUrl = websiteInfo.website_url || 'https://devnarfoundationfortheblind.org';
        return `${baseUrl.replace(/\/$/, '')}${logoUrl}`;
      }
      if (logoUrl.startsWith('http://') || logoUrl.startsWith('https://')) {
        return logoUrl;
      }
      return logoUrl;
    }
    
    // Fallback to default logo from assets
    return defaultLogo;
  };

  // Scroll handler with throttling
  useEffect(() => {
    let ticking = false;
    
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setScrolled(window.scrollY > 10);
          ticking = false;
        });
        ticking = true;
      }
    };
    
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close mobile nav on route change
  useEffect(() => {
    setNavOpen(false);
    setOpenSub(null);
  }, [location.pathname]);

  // Close nav on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target) && !e.target.closest('.menu-toggle')) {
        setNavOpen(false);
        setOpenSub(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const isActive = (href) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  const isSubActive = (subItems) => {
    return subItems.some(([_, href]) => location.pathname === href);
  };

  const logoUrl = getLogoUrl();
  const displayName = websiteInfo?.app_name || defaultName;

  return (
    <header ref={headerRef} className={`site-header ${scrolled ? 'scrolled' : ''}`}>
      <div className="wrap header-inner">
        <Link className="brand" to="/" onClick={() => setNavOpen(false)}>
          <img 
            src={logoUrl} 
            alt={displayName}
            onError={(e) => {
              // If logo fails to load, use default from assets
              e.target.onerror = null;
              e.target.src = defaultLogo;
              setLogoError(true);
            }}
          />
        </Link>

        <button
          className="menu-toggle"
          aria-label={navOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={navOpen}
          onClick={() => setNavOpen((o) => !o)}
        >
          {navOpen ? <IconClose /> : <IconMenu />}
        </button>

        <nav ref={navRef} className={`site-nav ${navOpen ? 'open' : ''}`} aria-label="Primary">
          <ul className="nav-list">
            {NAV_ITEMS.map((item) => {
              const active = item.sub ? isSubActive(item.sub) : isActive(item.href);
              const isOpen = openSub === item.label;

              return (
                <li
                  key={item.label}
                  className={`nav-item ${item.sub ? 'has-sub' : ''} ${isOpen ? 'open' : ''} ${active ? 'active' : ''}`}
                >
                  {item.sub ? (
                    <>
                      <span className="nav-link-wrapper">
                        <a
                          href={item.href}
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(item.href);
                            setNavOpen(false);
                          }}
                          className={active ? 'active' : ''}
                        >
                          <span className="nav-label">{item.label}</span>
                          <button
                            type="button"
                            className="caret-btn"
                            aria-label={`Toggle ${item.label} submenu`}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setOpenSub((s) => (s === item.label ? null : item.label));
                            }}
                          >
                            <IconChevron open={isOpen} />
                          </button>
                        </a>
                      </span>
                      <ul className="submenu">
                        {item.sub.map(([label, href]) => (
                          <li key={label}>
                            <Link
                              to={href}
                              className={location.pathname === href ? 'active' : ''}
                              onClick={() => setNavOpen(false)}
                            >
                              <span className="sub-dot">•</span>
                              {label}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : (
                    <Link
                      to={item.href}
                      className={active ? 'active' : ''}
                      onClick={() => setNavOpen(false)}
                    >
                      <span className="nav-label">{item.label}</span>
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="header-actions">
          <Link className="btn btn-primary" to={donateHref}>
            Donate
          </Link>
        </div>
      </div>
    </header>
  );
}